import { lazy } from 'react';
import { Dashboard } from './views/Dashboard';
import { SupportPage } from './views/Support';
import { CreateAccount } from './views/Manage/Create';
import { CreateOperator } from './views/CreateOperator';
import { CitrusView } from './views/RemoteMonitoring';
import {
  AccountCircle,
  Apartment,
  CalendarToday,
  Create,
  Error,
  FormatListNumbered,
  InsertLink,
  MeetingRoom,
  PlaylistAddCheck,
  Search,
  SettingsApplications,
  Storage,
  TextFields,
  Tune,
  TvOutlined,
  ViewModule,
  Visibility,
} from '@material-ui/icons';
import { AppViewContext, AppViewType } from './contexts/AppViewContext';

const AdScheduler = lazy(
  () => import(/* webpackChunkName: "scheduler"*/ './views/AdScheduler')
);
const Analytics = lazy(
  () => import(/* webpackChunkName: "analytic"*/ './views/Analytics')
);
const AnalyticsHits = lazy(
  () => import(/* webpackChunkName: "analytic"*/ './views/Analytics/hits')
);
const AnalyticsErrors = lazy(
  () => import(/* webpackChunkName: "analytic"*/ './views/Analytics/errors')
);
const AnalyticsSearch = lazy(
  () => import(/* webpackChunkName: "analytic"*/ './views/Analytics/search')
);
const AnalyticsTranslations = lazy(
  () =>
    import(/* webpackChunkName: "analytic"*/ './views/Analytics/translations')
);
const CitrusSignage = lazy(
  () => import(/* webpackChunkName: "scheduler"*/ './views/CitrusSignage')
);
const DSScheduler = lazy(
  () => import(/* webpackChunkName: "scheduler"*/ './views/CitrusSignage/')
);
const Control = lazy(
  () => import(/* webpackChunkName: "control"*/ './views/Manage')
);
const ControlRoom = lazy(
  () => import(/* webpackChunkName: "control"*/ './views/Manage/Account')
);
const ManageOperator = lazy(
  () => import(/* webpackChunkName: "control"*/ './views/Manage/Operator')
);
const Overview = lazy(
  () => import(/* webpackChunkName: "control"*/ './views/Manage/Overview')
);
const Inventory = lazy(
  () => import(/* webpackChunkName: "inventory"*/ './views/Manage/Inventory')
);
const Infrastructure = lazy(
  () =>
    import(
      /* webpackChunkName: "inventory"*/ './views/Manage/Inventory/Infrastructure'
    )
);
const System = lazy(
  () => import(/* webpackChunkName: "control"*/ './views/Manage/System')
);

const GuestInternet = lazy(
  () =>
    import(
      /* webpackChunkName: "control"*/ './views/Manage/System/GuestInternet'
    )
);

const ControlProvision = lazy(
  () => import(/* webpackChunkName: "control"*/ './views/Manage/Provisioning')
);
const LateCheckout = lazy(
  () => import(/* webpackChunkName: "latecheckout"*/ './views/LateCheckout')
);
const Messaging = lazy(
  () => import(/* webpackChunkName: "messaging"*/ './views/Messaging')
);
const MessagingCreate = lazy(
  () => import(/* webpackChunkName: "messaging"*/ './views/Messaging/create')
);
const MessagingView = lazy(
  () => import(/* webpackChunkName: "messaging"*/ './views/Messaging/view')
);
const MessagingApprove = lazy(
  () => import(/* webpackChunkName: "messaging"*/ './views/Messaging/approve')
);
const Administrator = lazy(
  () =>
    import(/* webpackChunkName: "administrator"*/ './views/Administrator/index')
);
const StreamMonitoring = lazy(
  () => import(/* webpackChunkName: "monitoring"*/ './views/StreamMonitoring')
);
const DeviceMetrics = lazy(
  () =>
    import(/* webpackChunkName: "dserver"*/ './views/Analytics/DeviceMetrics')
);
const MenuTesting = lazy(
  () => import(/* webpackChunkName: "menutesting" */ './views/MenuTesting')
);

export const appRoutes: Route[] = [
  {
    path: '/DServer',
    navbarName: 'D-Server',
    operatorType: 'Hospitality',
    component: <DeviceMetrics />,
    permission: (user) => user.permissions.canHosDServerAnalytics,
    views: ['Operator'],
  },
  {
    path: '/DServer',
    navbarName: 'D-Server',
    operatorType: 'Residential',
    component: <DeviceMetrics />,
    permission: (user) => user.permissions.canResDServerAnalytics,
    views: ['Operator'],
  },
  {
    path: '/dashboard',
    navbarName: 'Dashboard',
    component: <Dashboard />,
    hidden: true,
  },
  {
    path: '/adscheduler',
    navbarName: 'Ad Scheduler',
    component: <AdScheduler />,
    operatorType: 'Hospitality',
    permission: (user) => user.permissions.canHosAdScheduler,
    views: ['Operator'],
  },
  {
    path: '/signage',
    navbarName: 'Citrus Signage',
    operatorType: 'Hospitality',
    component: <CitrusSignage />,
    views: ['Operator'],
    subRoutes: [
      {
        icon: <CalendarToday fontSize="large" />,
        path: '/signage/scheduler',
        navbarName: 'Scheduler',
        component: <DSScheduler />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosDsScheduler,
      },
      {
        icon: <InsertLink fontSize="large" />,
        path: 'https://quicklink.tangerineglobal.com',
        navbarName: 'QuickLink',
        permission: (user) => user.permissions.canHosDsQuicklink,
      },
      {
        icon: <InsertLink fontSize="large" />,
        path: 'https://adreset.tangerineglobal.com',
        navbarName: 'Password Reset',
        permission: (user) => user.permissions.canHosDsQuicklink,
      },
    ],
  },
  {
    path: '/manage/system/guest-internet',
    navbarName: 'Guest Internet',
    component: <GuestInternet />,
    permission: (user) => user.permissions.canHosWifiOverview,
    systems: ['Guest Internet'],
    views: ['Operator'],
    hidden: true,
  },
  {
    path: '/manage',
    navbarName: 'Manage',
    component: <Control />,
    operatorType: 'Hospitality',
    views: ['NDP', 'RDP', 'Operator'],
    subRoutes: [
      {
        icon: <MeetingRoom fontSize="large" />,
        path: '/manage/account',
        navbarName: 'Room',
        component: <ControlRoom />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosRoom,
      },
      {
        path: '/manage/account/:accountId',
        navbarName: 'Room',
        component: <ControlRoom />,
        permission: (user) => user.permissions.canHosRoom,
        views: ['Operator'],
        hidden: true,
      },
      {
        icon: <ViewModule fontSize="large" />,
        path: '/manage/overview',
        navbarName: 'Overview',
        component: <Overview />,
        permission: (user) => user.permissions.canHosBulk,
        views: ['Operator'],
        operatorType: 'Hospitality',
      },
      {
        icon: <Tune fontSize="large" />,
        path: '/manage/system',
        navbarName: 'System',
        component: <System />,
        permission: (user) => user.permissions.canHosWifiOverview,
        systems: ['Guest Internet'],
        views: ['Operator'],
      },
      {
        icon: <FormatListNumbered fontSize="large" />,
        path: '/manage/inventory',
        navbarName: 'Inventory',
        component: <Inventory />,
        permission: (user) => user.permissions.canHosInventory,
        views: ['Operator'],
      },
      {
        icon: <Storage fontSize="large" />,
        path: '/manage/infrastructure',
        navbarName: 'Infrastructure',
        component: <Infrastructure />,
        views: ['NDP', 'RDP', 'Operator'],
        permission: (user) => user.permissions.canHosInfrastructure,
      },
      {
        icon: <Apartment fontSize="large" />,
        path: '/manage/operator',
        navbarName: 'Resort',
        component: <ManageOperator />,
        permission: (user) => user.permissions.canHosControlProperty,
        views: ['Operator'],
      },
      {
        icon: <SettingsApplications fontSize="large" />,
        path: '/manage/provision',
        navbarName: 'Provision',
        component: <ControlProvision />,
        permission: (user) => user.permissions.canHosProvision,
        views: ['Operator'],
      },
    ],
  },
  {
    path: '/manage',
    navbarName: 'Manage',
    component: <Control />,
    operatorType: 'Residential',
    views: ['NDP', 'RDP', 'Operator'],
    subRoutes: [
      {
        icon: <AccountCircle fontSize="large" />,
        path: '/manage/account',
        navbarName: 'Subscriber',
        component: <ControlRoom />,
        views: ['Operator'],
      },
      {
        path: '/manage/account/:accountId',
        navbarName: 'Subscriber',
        component: <ControlRoom />,
        hidden: true,
        views: ['Operator'],
      },
      {
        icon: <Create fontSize="large" />,
        path: '/manage/create',
        navbarName: 'Create',
        component: <CreateAccount />,
        views: ['Operator'],
        permission: (user) => user.permissions.canResAcctCreate,
      },
      {
        icon: <FormatListNumbered fontSize="large" />,
        path: '/manage/inventory',
        navbarName: 'Inventory',
        component: <Inventory />,
        permission: (user) => user.permissions.canResInventory,
        views: ['Operator'],
      },
      {
        icon: <Storage fontSize="large" />,
        path: '/manage/infrastructure',
        navbarName: 'Infrastructure',
        component: <Infrastructure />,
        views: ['NDP', 'RDP', 'Operator'],
        permission: (user) => user.permissions.canResInfrastructure,
      },
      {
        icon: <SettingsApplications fontSize="large" />,
        path: '/manage/operator',
        navbarName: 'Operator',
        component: <ManageOperator />,
        views: ['Operator'],
        permission: (user) =>
          user.permissions.canResOpRoutingGroups ||
          user.permissions.canResOpExperienceGroups ||
          user.permissions.canResOpChannelMapGroups ||
          user.permissions.canResOpCreditLimit ||
          user.permissions.canResOpPackages ||
          user.permissions.canResOpModels ||
          user.permissions.canResOpAppkeys ||
          user.permissions.canResOpFips ||
          user.permissions.canResOpCloudDvrs ||
          user.permissions.canResOpTransport ||
          user.permissions.canResOpIpBlock,
      },
    ],
  },
  {
    path: '/menu',
    navbarName: 'Guest Menu',
    operatorType: 'Hospitality',
    component: <Analytics />,
    views: ['Operator'],
    subRoutes: [
      {
        icon: <Visibility fontSize="large" />,
        path: '/menu/hits',
        navbarName: 'Hits',
        component: <AnalyticsHits />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMenuHits,
      },
      {
        icon: <Error fontSize="large" />,
        path: '/menu/errors',
        navbarName: 'Errors',
        component: <AnalyticsErrors />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMenuErrors,
      },
      {
        icon: <Search fontSize="large" />,
        path: '/menu/search',
        navbarName: 'Search',
        component: <AnalyticsSearch />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMenuSearch,
      },
      {
        icon: <TextFields fontSize="large" />,
        path: '/menu/translations',
        navbarName: 'Translations',
        component: <AnalyticsTranslations />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMenuTranslations,
      },
      {
        icon: <TvOutlined fontSize="large" />,
        path: '/menu/testing',
        navbarName: 'Menu Testing',
        component: <MenuTesting />,
        views: ['Operator'],
        permission: ({ permissions }) =>
          permissions.canHosMenuTesting || permissions.canHosConfigureSimulator,
      },
    ],
  },
  {
    path: '/lateCheckout',
    navbarName: 'Late Checkout',
    operatorType: 'Hospitality',
    component: <LateCheckout />,
    views: ['Operator'],
    permission: (user) =>
      user.permissions.canHosLateCheckoutConfig ||
      user.permissions.canHosLateCheckoutView,
  },
  {
    path: '/lateCheckout/:view',
    navbarName: 'Late Checkout',
    operatorType: 'Hospitality',
    component: <LateCheckout />,
    hidden: true,
    views: ['Operator'],
    permission: (user) =>
      user.permissions.canHosLateCheckoutConfig ||
      user.permissions.canHosLateCheckoutView,
  },
  {
    path: '/messaging',
    navbarName: 'Messaging',
    component: <Messaging />,
    operatorType: 'Hospitality',
    views: ['Operator'],
    subRoutes: [
      {
        icon: <Create fontSize="large" />,
        path: '/messaging/create',
        navbarName: 'Create',
        component: <MessagingCreate />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMessagingCreate,
      },
      {
        icon: <Visibility fontSize="large" />,
        path: '/messaging/view',
        navbarName: 'View',
        component: <MessagingView />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMessagingView,
      },
      {
        icon: <PlaylistAddCheck fontSize="large" />,
        path: '/messaging/approve',
        navbarName: 'Approve',
        component: <MessagingApprove />,
        views: ['Operator'],
        permission: (user) => user.permissions.canHosMessagingApprove,
      },
    ],
  },
  {
    path: '/messaging',
    navbarName: 'Messaging',
    component: <Messaging />,
    operatorType: 'Residential',
    views: ['Operator'],
    subRoutes: [
      {
        icon: <Create fontSize="large" />,
        path: '/messaging/create',
        navbarName: 'Create',
        component: <MessagingCreate />,
        views: ['Operator'],
        permission: (user) => user.permissions.canResMessagingCreate,
      },
      {
        icon: <Visibility fontSize="large" />,
        path: '/messaging/view',
        navbarName: 'View',
        component: <MessagingView />,
        views: ['Operator'],
        permission: (user) => user.permissions.canResMessagingView,
      },
    ],
  },
  {
    path: '/monitoring',
    navbarName: 'Monitoring',
    component: <StreamMonitoring />,
    views: ['NDP', 'Operator'],
    permission: (user) => user.permissions.canResViewSencore,
    operatorType: 'Residential',
  },
  {
    path: '/monitoring',
    navbarName: 'Monitoring',
    component: <StreamMonitoring />,
    views: ['NDP', 'RDP', 'Operator'],
    permission: (user) => user.permissions.canHosViewSencore,
    operatorType: 'Hospitality',
  },
  {
    path: '/admin',
    navbarName: 'Administrator',
    component: <Administrator />,
    views: ['Operator'],
    permission: (user) =>
      user.permissions.canUsers || user.permissions.canRoles,
    subRoutes: [
      {
        path: '/admin/:page',
        navbarName: 'Administrator',
        component: <Administrator />,
        views: ['Operator'],
        permission: (user) =>
          user.permissions.canUsers || user.permissions.canRoles,
      },
    ],
  },
  {
    path: '/support',
    navbarName: 'Support',
    component: <SupportPage />,
    views: ['Operator'],
    permission: (user) =>
      user.permissions.canHosSupportView ||
      user.permissions.canHosSupportCreate,
    operatorType: 'Hospitality',
  },
  {
    path: '/support',
    navbarName: 'Support',
    component: <SupportPage />,
    views: ['Operator'],
    permission: (user) =>
      user.permissions.canResSupportCreate ||
      user.permissions.canResSupportView,
    operatorType: 'Residential',
  },
  {
    path: '/createOperator',
    hidden: true,
    navbarName: 'Create Operator',
    component: <CreateOperator />,
    views: ['NDP', 'RDP', 'Operator'],
    permission: (user) =>
      user.permissions.canHosControlProperty || user.permissions.canResOpCreate,
  },
  {
    path: '/citrusView',
    navbarName: 'CITRUS View',
    component: <CitrusView />,
    views: ['Operator'],
    permission: (user) => user.permissions.canResCitrusView,
  },
];

export const ROUTE_VALIDATION = (
  route: Route,
  view: AppViewContext,
  operator: Operator,
  user: UserContext
): boolean => {
  if (!route.component) return false;
  if (route.operatorType && route.operatorType !== view.sector) return false;
  if (route.permission && !route.permission(user)) return false;
  if (
    route.views &&
    route.views.length > 0 &&
    !route.views.some((v: AppViewType) => v === view.type)
  )
    return false;

  const hasSystems = operator.systems.some(
    (s) => s.active && (route.systems ?? []).includes(s.name)
  );

  if (route.systems !== undefined && !hasSystems) return false;

  return true;
};
